/**
 * Piezas comunes de los formatos imprimibles NO fiscales (nota de entrega, cotización, despacho): membrete con los dos
 * logos de la empresa y leyendas editables. La factura no los usa: su papel ya trae la cabecera de la imprenta.
 */
import type { EmpresaImpresionDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api.ts';

/** Datos de la empresa para la cabecera de los formatos (los mismos para todas las hojas de impresión). */
export function useEmpresaImpresion() {
  return useQuery({ queryKey: ['empresa'], queryFn: () => get<EmpresaImpresionDTO>('/sucursales/empresa') });
}

interface Sede {
  nombre: string;
  direccion_fiscal: string;
  /** Ya no sale en el membrete (salen todas las tiendas): se acepta para no cambiar a quien lo pasa. */
  telefono?: string | null;
}

/**
 * Teléfonos del membrete: cada tienda activa con su fijo y su WhatsApp («Barquisimeto: 0251-… · WhatsApp 0426-…»). Si
 * ninguna tiene número cargado, el teléfono de la empresa. Antes juntaba el de la sucursal con el de la empresa y salía
 * repetido con un número viejo (pedido de la dueña 2026-10-19).
 */
export function telefonosMembrete(empresa: Pick<EmpresaImpresionDTO, 'telefono' | 'tiendas'>): string[] {
  const tiendas = (empresa.tiendas ?? [])
    .map((t) => ({ nombre: t.nombre, numeros: [t.telefono?.trim(), t.whatsapp?.trim() ? `WhatsApp ${t.whatsapp.trim()}` : null].filter((x): x is string => !!x) }))
    .filter((t) => t.numeros.length > 0)
    .map((t) => `${t.nombre}: ${t.numeros.join(' · ')}`);
  if (tiendas.length > 0) return tiendas;
  return empresa.telefono?.trim() ? [`Telfs: ${empresa.telefono.trim()}`] : [];
}

/**
 * Membrete: logo nuevo a la izquierda, razón social, dirección, RIF y teléfonos al centro, logo anterior a la derecha
 * (respuesta de la clienta 2026-10-09: «los dos logos en los formatos»). `sede` es la sucursal que emite el documento.
 */
export function Membrete({ empresa, sede, compacto = false }: { empresa: EmpresaImpresionDTO; sede?: Sede | null | undefined; compacto?: boolean }) {
  const direccion = sede?.direccion_fiscal || empresa.direccion_fiscal;
  const telefonos = telefonosMembrete(empresa);
  const alto = compacto ? 46 : 68;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compacto ? 6 : 12, borderBottom: '2px solid #000', paddingBottom: compacto ? 3 : 6, marginBottom: compacto ? 4 : 6 }} data-testid="formato-membrete">
      <img src="/logo-mg.png" alt="MG Materiales y Decoraciones" style={{ height: alto, width: 'auto' }} data-testid="formato-logo-nuevo" />
      <div style={{ flex: 1, textAlign: 'center', lineHeight: 1.25 }}>
        <div style={{ fontSize: compacto ? 11.5 : 15, fontWeight: 'bold', fontStyle: 'italic' }}>{empresa.razon_social.toUpperCase()}</div>
        <div>{direccion}</div>
        <div>RIF: {empresa.rif}</div>
        {/* Una tienda por tramo: si no caben en una línea, se parte entre tiendas y no en medio de un número */}
        {telefonos.length > 0 ? (
          <div data-testid="formato-telefonos">
            {telefonos.map((t, i) => (
              <span key={t}>
                {i > 0 ? ' | ' : null}
                <span style={{ whiteSpace: 'nowrap' }}>{t}</span>
              </span>
            ))}
          </div>
        ) : null}
        {sede ? <div style={{ fontSize: '0.9em' }}>Sucursal {sede.nombre}</div> : null}
      </div>
      <img src="/logo-mg-antiguo.png" alt="Materiales y Decoraciones M.G. C.A." style={{ height: alto * 0.56, width: 'auto' }} data-testid="formato-logo-antiguo" />
    </div>
  );
}

/** Leyendas al pie (cambios, cortes, retiro…): solo las que tienen texto. */
export function Leyendas({ textos, tamano = 9.5 }: { textos: (string | undefined)[]; tamano?: number }) {
  const lista = textos.filter((t): t is string => !!t && t.trim().length > 0);
  if (lista.length === 0) return null;
  return (
    <ul style={{ margin: '8px 0 0', paddingLeft: 14, fontSize: tamano }} data-testid="formato-leyendas">
      {lista.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}
